import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Lock, Loader2, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const { theme, setTheme } = useTheme();
  const previousThemeRef = useRef(theme);
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  
  const [form, setForm] = useState({
    password: '',
    confirmPassword: ''
  });

  // Force dark mode on this page
  useEffect(() => {
    previousThemeRef.current = theme;
    if (theme !== 'dark') {
      setTheme('dark');
    }
    return () => {
      if (previousThemeRef.current !== 'dark') {
        setTheme(previousThemeRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenError('No reset token provided');
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API_URL}/auth/validate-reset-token/${token}`);
        setTokenValid(true);
        setTokenInfo(response.data);
      } catch (error) {
        setTokenError(error.response?.data?.detail || 'Invalid or expired reset link');
      } finally {
        setLoading(false);
      }
    };
    
    validateToken();
  }, [token]);

  const validatePassword = () => {
    if (form.password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/\d/.test(form.password)) {
      return 'Password must contain at least one number';
    }
    if (form.password !== form.confirmPassword) {
      return 'Passwords do not match';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validatePassword();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        token,
        new_password: form.password
      });
      
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_job-tracker-app-6/artifacts/penuw891_iStock-1174890965.jpg)',
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <Card className="w-full max-w-md relative z-10 border-0 shadow-2xl">
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid && !success) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_job-tracker-app-6/artifacts/penuw891_iStock-1174890965.jpg)',
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
        
        <Card className="w-full max-w-md relative z-10 border-0 shadow-2xl" data-testid="invalid-token-card">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertCircle className="h-8 w-8 text-red-500" />
              </div>
            </div>
            <CardTitle className="font-heading text-2xl">Link Invalid</CardTitle>
            <CardDescription>{tokenError}</CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            
            <div className="flex flex-col gap-2">
              <Button
                className="w-full btn-volt"
                onClick={() => navigate('/forgot-password')}
                data-testid="request-new-link-button"
              >
                Request New Link
              </Button>
              
              <Link
                to="/login"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center justify-center gap-1 py-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_job-tracker-app-6/artifacts/penuw891_iStock-1174890965.jpg)',
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
        
        <Card className="w-full max-w-md relative z-10 border-0 shadow-2xl" data-testid="success-card">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
            </div>
            <CardTitle className="font-heading text-2xl">Password Reset!</CardTitle>
            <CardDescription>Your password has been successfully changed.</CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              You can now log in with your new password.
            </p>
            
            <Button
              className="w-full btn-volt"
              onClick={() => navigate('/login')}
              data-testid="go-to-login-button"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Reset form
  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center relative"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_job-tracker-app-6/artifacts/penuw891_iStock-1174890965.jpg)',
      }}
    >
      <div className="absolute inset-0 bg-black/60" />
      
      <Card className="w-full max-w-md relative z-10 border-0 shadow-2xl" data-testid="reset-password-card">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-xl overflow-hidden">
              <img src="/equipr-logo.png" alt="EQUIPR" className="w-full h-full object-cover" />
            </div>
          </div>
          <CardTitle className="font-heading text-2xl">Reset Password</CardTitle>
          <CardDescription>
            {tokenInfo?.user_name 
              ? `Hi ${tokenInfo.user_name}, create your new password`
              : 'Create your new password'
            }
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="label-overline">New Password</Label>
              <div className="relative">
                <Input
                  data-testid="password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Enter new password"
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                Minimum 8 characters, must include at least one number
              </p>
            </div>

            <div className="space-y-2">
              <Label className="label-overline">Confirm Password</Label>
              <div className="relative">
                <Input
                  data-testid="confirm-password-input"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={form.confirmPassword}
                  onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                  placeholder="Confirm new password"
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Password requirements indicator */}
            <div className="bg-muted/50 rounded-md p-3 space-y-1">
              <p className="text-xs font-medium text-muted-foreground mb-2">Password requirements:</p>
              <div className="flex items-center gap-2">
                <div className={`h-1.5 w-1.5 rounded-full ${form.password.length >= 8 ? 'bg-green-500' : 'bg-muted-foreground'}`} />
                <span className={`text-xs ${form.password.length >= 8 ? 'text-green-500' : 'text-muted-foreground'}`}>
                  At least 8 characters
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`h-1.5 w-1.5 rounded-full ${/\d/.test(form.password) ? 'bg-green-500' : 'bg-muted-foreground'}`} />
                <span className={`text-xs ${/\d/.test(form.password) ? 'text-green-500' : 'text-muted-foreground'}`}>
                  Contains a number
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`h-1.5 w-1.5 rounded-full ${form.password && form.password === form.confirmPassword ? 'bg-green-500' : 'bg-muted-foreground'}`} />
                <span className={`text-xs ${form.password && form.password === form.confirmPassword ? 'text-green-500' : 'text-muted-foreground'}`}>
                  Passwords match
                </span>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full btn-volt" 
              disabled={submitting}
              data-testid="submit-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <Lock className="h-4 w-4 mr-2" />
                  Reset Password
                </>
              )}
            </Button>

            <div className="pt-4 border-t text-center">
              <Link
                to="/login"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to login
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
